import { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  useTheme,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  alpha,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Fade,
} from "@mui/material";
import { Plus, Trash2, X } from "lucide-react";
import SearchIcon from "@mui/icons-material/Search";
import PageTitle from "../../components/PageTitle";
import AddTeamModal from "./AddTeamModal";
import TeamsLoader from "./TeamsLoader";
import TeamsNoData from "./TeamsNoData";
import { teamCapabilities } from "../../config/teamCapabilities";
import {
  getAllTeamsApi,
  createTeamApi,
  updateTeamApi,
  deleteTeamApi,
} from "../../api/teams.api";

export default function Teams() {
  const theme = useTheme();
  const [teams, setTeams] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [capabilitiesModal, setCapabilitiesModal] = useState({
    open: false,
    team: null,
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        setLoading(true);
        const response = await getAllTeamsApi();
        setTeams(response.data.data || []);
      } catch (err) {
        console.error("Failed to load teams:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTeams();
  }, []);

  const handleCreateTeam = async (teamData) => {
    try {
      const response = await createTeamApi(teamData);
      const newTeam = response.data.data;
      setTeams((prev) => [...prev, newTeam]);
      setOpenModal(false);
      setEditingTeam(null);
    } catch (err) {
      console.error("Failed to create team:", err);
    }
  };

  const handleUpdateTeam = async (teamId, teamData) => {
    try {
      const response = await updateTeamApi(teamId, teamData);
      const updatedTeam = response.data.data;
      setTeams((prev) =>
        prev.map((team) => (team._id === teamId ? updatedTeam : team))
      );
      setOpenModal(false);
      setEditingTeam(null);
    } catch (err) {
      console.error("Failed to update team:", err);
    }
  };

  const handleModalSubmit = async (...args) => {
    if (args.length === 2) {
      await handleUpdateTeam(args[0], args[1]);
    } else {
      await handleCreateTeam(args[0]);
    }
  };

  const handleManageTeam = (team) => {
    setEditingTeam(team);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingTeam(null);
  };

  const handleDeleteTeam = async (teamId) => {
    try {
      await deleteTeamApi(teamId);
      setTeams((prev) => prev.filter((team) => team._id !== teamId));
    } catch (err) {
      console.error("Failed to delete team:", err);
    }
  };

  const filteredTeams = teams.filter((team) =>
    team.name.toLowerCase().includes(search.toLowerCase())
  );

  const getCapabilityById = (id) => {
    return teamCapabilities.find((cap) => cap.id === id);
  };

  return (
    <Fade in={mounted} timeout={500}>
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          "@keyframes slideUp": {
            from: { opacity: 0, transform: "translateY(12px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
          animation:
            "slideUp 0.45s cubic-bezier(0.23, 1, 0.32, 1) forwards",
        }}
      >
        {/* Header */}
        <Box
          mb={3}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
          }}
        >
          <PageTitle parts={["Teams"]} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <TextField
              size="small"
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      fontSize="small"
                      sx={{ color: "#9CA3AF", ml: 0.5 }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: 240, md: 280 },
                bgcolor: "#fff",
                borderRadius: 3,
                transition: "box-shadow 0.25s ease",
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  "& fieldset": { borderColor: "#E5E7EB" },
                  "&:hover fieldset": {
                    borderColor: theme.palette.primary.main,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.primary.main,
                    borderWidth: "1.5px",
                  },
                },
                "&:hover": {
                  boxShadow: `0 0 0 3px ${theme.palette.primary.main}18`,
                },
              }}
            />

            <Button
              onClick={() => {
                setEditingTeam(null);
                setOpenModal(true);
              }}
              startIcon={<Plus size={16} />}
              sx={{
                borderRadius: 3,
                px: { xs: 2, sm: 3 },
                py: { xs: 1, sm: 1.2 },
                fontSize: { xs: 13, sm: 14 },
                fontWeight: 700,
                textTransform: "none",
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.avatarGradients_1.avatarEnd} 100%)`,
                color: "#fff",
                boxShadow: `0 4px 14px ${theme.palette.primary.main}30`,
                width: { xs: "100%", sm: "auto" },
                transition:
                  "all 0.25s cubic-bezier(0.23, 1, 0.32, 1)",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: `0 6px 20px ${theme.palette.primary.main}40`,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.avatarGradients_1.avatarEnd} 100%)`,
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Add Team
            </Button>
          </Stack>
        </Box>

        {/* Teams Grid */}
        {loading ? (
          <TeamsLoader />
        ) : filteredTeams.length === 0 ? (
          <TeamsNoData
            search={search}
            onCreateTeam={() => setOpenModal(true)}
          />
        ) : (
          <Grid container spacing={3}>
            {filteredTeams.map((team, idx) => {
              const capCount = team.capabilities?.length || 0;
              return (
                <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={team._id}>
                  <Fade in timeout={400 + idx * 80}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        position: "relative",
                        transition:
                          "all 0.3s cubic-bezier(0.23, 1, 0.32, 1)",
                        border: "1px solid #F0F0F0",
                        bgcolor: "#fff",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        boxShadow:
                          "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
                        "&:hover": {
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transform: "translateY(-4px)",
                        },
                      }}
                    >
                      {/* Delete Button */}
                      <IconButton
                        onClick={() => handleDeleteTeam(team._id)}
                        sx={{
                          position: "absolute",
                          top: 12,
                          right: 12,
                          width: 32,
                          height: 32,
                          borderRadius: 2,
                          bgcolor: alpha("#ef4444", 0.08),
                          color: "#ef4444",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor: alpha("#ef4444", 0.18),
                            transform: "scale(1.08)",
                          },
                        }}
                      >
                        <Trash2 size={16} />
                      </IconButton>

                      <CardContent
                        sx={{
                          p: 3,
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                          "&:last-child": { pb: 3 },
                        }}
                      >
                        {/* Team Icon */}
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
                            border: `1.5px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            mb: 2,
                          }}
                        >
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke={theme.palette.primary.main}
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M3 3v18h18" />
                            <path d="M7 16l4-8 4 4 4-6" />
                          </svg>
                        </Box>

                        {/* Team Name & Members */}
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: 18,
                            color: "#1F2937",
                            mb: 0.25,
                            letterSpacing: "-0.02em",
                          }}
                        >
                          {team.name}
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: 13,
                            color: "#9CA3AF",
                            mb: 2.5,
                          }}
                        >
                          Members:{" "}
                          {team.members?.length || team.memberCount || 0}
                        </Typography>

                        {/* Capabilities Label */}
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.1em",
                            color: theme.palette.primary.main,
                            mb: 1.5,
                          }}
                        >
                          Capabilities ({capCount})
                        </Typography>

                        {/* Capability Chips */}
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            mb: 3,
                            flex: 1,
                          }}
                        >
                          {(team.capabilities || [])
                            .slice(0, 6)
                            .map((capId) => {
                              const capability =
                                getCapabilityById(capId);
                              if (!capability) return null;

                              const Icon = capability.icon;
                              return (
                                <Chip
                                  key={capId}
                                  icon={
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <Icon
                                        size={13}
                                        color={capability.color}
                                      />
                                    </Box>
                                  }
                                  label={capability.label}
                                  size="small"
                                  sx={{
                                    height: 26,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    bgcolor: alpha(
                                      capability.color,
                                      0.08
                                    ),
                                    color: capability.color,
                                    border: `1px solid ${alpha(capability.color, 0.18)}`,
                                    borderRadius: 1.5,
                                    "& .MuiChip-icon": {
                                      ml: "6px",
                                    },
                                    "& .MuiChip-label": {
                                      px: 0.75,
                                    },
                                    transition: "all 0.2s ease",
                                  }}
                                />
                              );
                            })}

                          {capCount > 6 && (
                            <Chip
                              label={`+${capCount - 6}`}
                              size="small"
                              onClick={() =>
                                setCapabilitiesModal({
                                  open: true,
                                  team,
                                })
                              }
                              sx={{
                                height: 26,
                                fontSize: 11,
                                fontWeight: 700,
                                bgcolor: alpha(
                                  theme.palette.primary.main,
                                  0.1
                                ),
                                color: theme.palette.primary.main,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                                borderRadius: 1.5,
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.18
                                  ),
                                  transform: "scale(1.05)",
                                },
                              }}
                            />
                          )}
                        </Box>

                        {/* Manage Team Button */}
                        <Box mt="auto">
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={() => handleManageTeam(team)}
                            sx={{
                              py: 1.25,
                              fontWeight: 700,
                              fontSize: 13,
                              letterSpacing: "0.06em",
                              textTransform: "uppercase",
                              borderRadius: 2.5,
                              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.avatarGradients_1.avatarEnd} 100%)`,
                              color: "#fff",
                              boxShadow: `0 4px 14px ${theme.palette.primary.main}25`,
                              transition:
                                "all 0.25s cubic-bezier(0.23, 1, 0.32, 1)",
                              "&:hover": {
                                transform: "translateY(-1px)",
                                boxShadow: `0 6px 20px ${theme.palette.primary.main}35`,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.avatarGradients_1.avatarEnd} 100%)`,
                              },
                              "&:active": {
                                transform: "translateY(0)",
                              },
                            }}
                          >
                            Manage Team
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Fade>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Capabilities Modal */}
        <Dialog
          open={capabilitiesModal.open}
          onClose={() =>
            setCapabilitiesModal({ open: false, team: null })
          }
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: "#fff",
            },
          }}
        >
          <DialogTitle
            sx={{
              pb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, fontSize: 18 }}
              >
                All Capabilities
              </Typography>
              {capabilitiesModal.team && (
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    mt: 0.5,
                  }}
                >
                  {capabilitiesModal.team.name}
                </Typography>
              )}
            </Box>
            <IconButton
              onClick={() =>
                setCapabilitiesModal({ open: false, team: null })
              }
              sx={{
                color: theme.palette.text.secondary,
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              <X size={20} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pb: 3 }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                mt: 1,
              }}
            >
              {capabilitiesModal.team?.capabilities.map((capId) => {
                const capability = getCapabilityById(capId);
                if (!capability) return null;

                const Icon = capability.icon;
                return (
                  <Chip
                    key={capId}
                    icon={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Icon size={14} color={capability.color} />
                      </Box>
                    }
                    label={capability.label}
                    size="small"
                    sx={{
                      height: 28,
                      minWidth: 110,
                      fontSize: 11,
                      fontWeight: 600,
                      bgcolor: alpha(capability.color, 0.08),
                      color: theme.palette.text.primary,
                      border: `1px solid ${alpha(capability.color, 0.2)}`,
                      "& .MuiChip-icon": {
                        marginLeft: "6px",
                      },
                      "& .MuiChip-label": {
                        px: 1,
                      },
                    }}
                  />
                );
              })}
            </Box>
          </DialogContent>
        </Dialog>

        {/* Add Team Modal */}
        <AddTeamModal
          open={openModal}
          onClose={handleCloseModal}
          onSubmit={handleModalSubmit}
          editTeam={editingTeam}
        />
      </Box>
    </Fade>
  );
}
